import { useRef, useState } from "react"

import {
  AI_ACTION_VALUES,
  AskQuestionEvalMockInput,
  ESCALATING_AI_ACTIONS,
  PLATFORM_TYPES,
  createAiEvalMock
} from "@domain/ai_eval_mocks"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography
} from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"

type Props = {
  open: boolean
  onClose: () => void
  onImported: () => void
}

const PLACEHOLDER = `[
  {
    "title": "Kargo ücreti soruluyor",
    "cases": [
      {
        "query": "Kargo ücretsiz mi?",
        "assertions": {
          "expectedAiAction": "positive",
          "expectedContentIncludes": ["ücretsiz kargo", "free shipping"]
        }
      }
    ]
  },
  {
    "title": "Kapsam dışı soru insana devredilir",
    "cases": [
      {
        "query": "Bugün hava nasıl?",
        "assertions": { "expectedAiAction": "humanSupport" }
      }
    ]
  }
]

// expectedAiAction: ${AI_ACTION_VALUES.join(" | ")}
// expectedContentIncludes: en az biri geçerse yeterli`

function parseMocks(raw: string): AskQuestionEvalMockInput[] {
  const parsed = JSON.parse(raw)
  const list = Array.isArray(parsed) ? parsed : parsed?.mocks
  if (!Array.isArray(list)) {
    throw new Error(
      "JSON bir dizi olmalı (ya da içinde 'mocks' dizisi barındıran bir obje)"
    )
  }
  list.forEach((item, index) => {
    if (!item || typeof item !== "object") {
      throw new Error(`#${index + 1}. kayıt geçerli bir obje değil`)
    }
    if (!item.title || typeof item.title !== "string") {
      throw new Error(`#${index + 1}. kayıtta "title" eksik`)
    }
    if (!Array.isArray(item.cases) || item.cases.length === 0) {
      throw new Error(
        `#${index + 1}. kayıtta ("${item.title}") en az 1 case olmalı`
      )
    }
    const channelType = (item as { channelType?: unknown }).channelType
    if (channelType && !PLATFORM_TYPES.includes(channelType as never)) {
      throw new Error(
        `#${index + 1}. kayıt ("${item.title}"): "${String(channelType)}" geçerli bir kanal değil. channelType bir platform olmalı (ör. LIVE_CHAT, INSTAGRAM) — mesaj tipi değil.`
      )
    }
    item.cases.forEach((c: unknown, ci: number) => {
      if (!c || typeof c !== "object" || !(c as { query?: unknown }).query) {
        throw new Error(
          `#${index + 1}. kayıt ("${item.title}"), case #${ci + 1} için "query" eksik`
        )
      }
      // Backend rejects these on write; catch them here so the whole import doesn't half-fail.
      const action = (c as { assertions?: { expectedAiAction?: string } })
        .assertions?.expectedAiAction
      if (action && !AI_ACTION_VALUES.includes(action as never)) {
        throw new Error(
          `#${index + 1}. kayıt ("${item.title}"), case #${ci + 1}: geçersiz expectedAiAction "${action}". İzin verilen: ${AI_ACTION_VALUES.join(", ")}`
        )
      }
    })
  })
  return list as AskQuestionEvalMockInput[]
}

export function ImportJsonDialog({ open, onClose, onImported }: Props) {
  const [raw, setRaw] = useState("")
  const [parseError, setParseError] = useState<string | null>(null)
  const [preview, setPreview] = useState<AskQuestionEvalMockInput[] | null>(
    null
  )
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setRaw("")
    setParseError(null)
    setPreview(null)
    setProgress({ done: 0, total: 0 })
  }

  const handleClose = () => {
    if (importing) return
    reset()
    onClose()
  }

  const handleChange = (value: string) => {
    setRaw(value)
    setPreview(null)
    setParseError(null)
    if (!value.trim()) return
    try {
      const mocks = parseMocks(value)
      setPreview(mocks)
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "JSON parse edilemedi")
    }
  }

  const handleFileUpload = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => handleChange(String(reader.result || ""))
    reader.readAsText(file)
  }

  const handleImport = async () => {
    if (!preview || preview.length === 0) return
    setImporting(true)
    setProgress({ done: 0, total: preview.length })
    let successCount = 0
    const failures: string[] = []

    for (const mock of preview) {
      const res = await createAiEvalMock(mock)
      if (res.isSuccess) {
        successCount++
      } else {
        failures.push(
          `"${mock.title}": ${res.errorMessage || "bilinmeyen hata"}`
        )
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }))
    }

    setImporting(false)

    if (failures.length === 0) {
      showSuccessToast(`${successCount} senaryo içe aktarıldı`)
      reset()
      onImported()
      onClose()
    } else {
      showErrorToast(
        `${successCount} senaryo içe aktarıldı, ${failures.length} kayıt başarısız oldu`
      )
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          JSON ile İçe Aktar
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Bir dizi senaryo JSON&apos;ı yapıştır ya da dosya yükle. Her kayıt
          ayrı bir test senaryosu olarak oluşturulur.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="flex-start"
        >
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={importing}
              sx={{ alignSelf: "flex-start" }}
            >
              Dosya Seç (.json)
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFileUpload(file)
                  e.target.value = ""
                }}
              />
            </Button>

            <TextField
              multiline
              minRows={10}
              maxRows={16}
              fullWidth
              value={raw}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={PLACEHOLDER}
              disabled={importing}
              sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
            />

            {parseError && <Alert severity="error">{parseError}</Alert>}

            {importing && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(progress.done / Math.max(progress.total, 1)) * 100}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {progress.done} / {progress.total} oluşturuldu
                </Typography>
              </Box>
            )}
          </Stack>

          {!parseError && preview && preview.length > 0 && (
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
              >
                Önizleme
              </Typography>
              <Alert severity="success" sx={{ mt: 1, mb: 1.5, py: 0.5 }}>
                {preview.length} senaryo,{" "}
                {preview.reduce((acc, m) => acc + m.cases.length, 0)} case
                bulundu
              </Alert>
              <Box
                sx={{
                  maxHeight: 420,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2
                }}
              >
                {preview.map((mock, index) => (
                  <Accordion
                    key={index}
                    disableGutters
                    square
                    defaultExpanded={preview.length === 1}
                    sx={{
                      "&:before": { display: "none" },
                      boxShadow: "none",
                      "&:not(:last-of-type)": {
                        borderBottom: "1px solid",
                        borderColor: "divider"
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="small" />}
                      sx={{
                        minHeight: 44,
                        "& .MuiAccordionSummary-content": { my: 1 }
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ minWidth: 0, width: "100%" }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1
                          }}
                        >
                          {index + 1}. {mock.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${mock.cases.length} case`}
                          variant="outlined"
                          sx={{ fontSize: 11, fontWeight: 600, flexShrink: 0 }}
                        />
                        {mock.channelType && (
                          <Chip
                            size="small"
                            label={mock.channelType}
                            sx={{ fontSize: 11, flexShrink: 0 }}
                          />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.5}>
                        {mock.cases.map((c, ci) => (
                          <Box key={ci}>
                            {ci > 0 && <Divider sx={{ mb: 1.5 }} />}
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {ci + 1}. {c.query}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {c.assertions?.expectedAiAction && (
                                <Chip
                                  size="small"
                                  color={
                                    ESCALATING_AI_ACTIONS.includes(
                                      c.assertions.expectedAiAction as never
                                    )
                                      ? "warning"
                                      : "success"
                                  }
                                  label={`action: ${c.assertions.expectedAiAction}`}
                                  sx={{ fontSize: 11 }}
                                />
                              )}
                              {c.assertions?.expectedStatus && (
                                <Chip
                                  size="small"
                                  label={`status: ${c.assertions.expectedStatus}`}
                                  sx={{ fontSize: 11 }}
                                />
                              )}
                              {(
                                c.assertions?.expectedContentIncludes || []
                              ).map((word, wi) => (
                                <Chip
                                  key={wi}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  label={word}
                                  sx={{ fontSize: 11 }}
                                />
                              ))}
                              {(c.assertions?.requiredSteps || []).map(
                                (step, si) => (
                                  <Chip
                                    key={si}
                                    size="small"
                                    variant="outlined"
                                    label={`✓ ${step}`}
                                    sx={{ fontSize: 11 }}
                                  />
                                )
                              )}
                              {(c.assertions?.forbiddenSteps || []).map(
                                (step, si) => (
                                  <Chip
                                    key={si}
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    label={`✗ ${step}`}
                                    sx={{ fontSize: 11 }}
                                  />
                                )
                              )}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={importing} color="inherit">
          İptal
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disableElevation
          disabled={importing || !preview || preview.length === 0}
        >
          {importing ? "İçe Aktarılıyor..." : "İçe Aktar"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
